import { useState, useEffect } from 'react';
import type { Filter } from '../types/filter';
import type { Game } from '../hooks/useGames';
import styles from './FilterPage.module.css';

// Filter ID constants
const FILTER_IDS = {
  VOLATILITY: 'volatility',
  FUNCTIONALITIES: 'functionalities',
  THEMES: 'themes',
  PROVIDERS: 'providers'
} as const;

interface FilterPageProps {
  filters: Filter[];
  games: Game[];
  onClose: () => void;
  onApplyFilters: (filteredGames: Game[], selectedFilters: Record<string, string[]>) => void;
  initialSelectedFilters?: Record<string, string[]>;
}

export default function FilterPage({ filters, games, onClose, onApplyFilters, initialSelectedFilters }: FilterPageProps) {
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(initialSelectedFilters || {});
  const [showInfo, setShowInfo] = useState<string>('');
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);

  // Filter games based on selected filters
  const filterGames = () => {
    let result: Game[] = games;
    
    if (Object.keys(selectedFilters).length === 0) {
      setFilteredGames([]);
      return;
    }

    // Apply filters in sequence (AND logic between different filter types)
    Object.entries(selectedFilters).forEach(([filterId, selectedValues]) => {
      if (selectedValues.length === 0) return;
      
      switch (filterId) {
        case FILTER_IDS.VOLATILITY:
          result = result.filter(game => 
            selectedValues.includes(game.info?.volatility || '')
          );
          break;
          
        case FILTER_IDS.FUNCTIONALITIES:
          result = result.filter(game => {
            const gameFunctionalities = game.info?.functionality || [];
            return gameFunctionalities.some(func => 
              selectedValues.includes(func)
            );
          });
          break;
          
        case FILTER_IDS.THEMES:
          result = result.filter(game => {
            const gameThemes = game.info?.theme || [];
            return gameThemes.some(theme => 
              selectedValues.includes(theme)
            );
          });
          break;
          
        case FILTER_IDS.PROVIDERS:
          result = result.filter(game => 
            selectedValues.includes(game.provider)
          );
          break;
          
        default:
          break;
      }
    });
    
    setFilteredGames(result);
  };

  // Run filtering whenever selected filters change
  useEffect(() => {
    filterGames();
  }, [selectedFilters, games]);

  const handleFilterToggle = (filterId: string, value: string) => {
    setSelectedFilters(prev => {
      const current = prev[filterId] || [];
      const isSelected = current.includes(value);
      
      if (isSelected) {
        return {
          ...prev,
          [filterId]: current.filter(v => v !== value)
        };
      } else {
        return {
          ...prev,
          [filterId]: [...current, value]
        };
      }
    });
  };

  const isFilterSelected = (filterId: string, value: string) => {
    return selectedFilters[filterId]?.includes(value) || false;
  };

  const resetFilters = () => {
    setSelectedFilters({});
    // Apply empty filters immediately to update the parent state
    onApplyFilters(games, {});
  };

  const getFilteredGamesCount = () => {
    // Only show count if there are selected filters with values
    const hasActiveFilters = Object.values(selectedFilters).some(values => values.length > 0);
    return hasActiveFilters ? filteredGames.length.toString() : '';
  };

  return (
    <div className={`fixed inset-0 z-[60] flex flex-col ${styles.filterPageBg}`}>
      {/* Header */}
      <div className="px-6 py-4">
        <div className="flex justify-end mb-2">
          <button
            onClick={onClose}
            className="p-1"
          >
            <img src="/close-modal@2x.png" alt="Chiudi" className="w-6 h-6" />
          </button>
        </div>
        <h1 className="text-2xl font-bold text-white mb-4">Filtri</h1>
        <div className={`border-b ${styles.divider}`}></div>
      </div>

      {/* Scrollable Filter sections */}
      <div className="flex-1 overflow-y-auto px-6">
        {filters.map((filter) => (
          <div key={filter.id} className="mb-6">
            {/* Section header */}
            <div className="flex items-start gap-2 mb-3">
              <h2 className="text-lg font-light text-white">{filter.label}</h2>
              {filter.info && (
                <button
                  onClick={() => setShowInfo(showInfo === filter.id ? '' : filter.id)}
                  className="mt-1"
                >
                  <img 
                    src="/info@2x.png" 
                    alt="Info" 
                    className="w-4 h-4"
                  />
                </button>
              )}
              {showInfo === filter.id && (
                <div className={`flex-1 ml-2 p-3 text-xs rounded-lg ${styles.infoTooltip}`}>
                  {filter.info}
                </div>
              )}
            </div>

            {/* Filter options */}
            {filter.id === FILTER_IDS.VOLATILITY ? (
              // Volatility as checkboxes
              <div className="flex flex-wrap gap-6">
                {filter.data.map((value) => (
                  <button
                    key={value}
                    onClick={() => handleFilterToggle(filter.id, value)}
                    className="flex items-center gap-2 text-sm hover:text-white transition-colors"
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isFilterSelected(filter.id, value)
                        ? styles.checkboxSelected
                        : styles.checkbox
                    }`}>
                      {isFilterSelected(filter.id, value) && (
                        <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className={isFilterSelected(filter.id, value) ? 'text-white font-semibold' : 'text-gray-300'}>
                      {value}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              // Other filters as pills
              <div className="flex flex-wrap gap-2">
                {filter.data.map((value) => (
                  <button
                    key={value}
                    onClick={() => handleFilterToggle(filter.id, value)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                      isFilterSelected(filter.id, value)
                        ? styles.filterButtonSelected
                        : styles.filterButton
                    }`}
                  >
                    {value}
                    {isFilterSelected(filter.id, value) && (
                      <img 
                        src="/remove@2x.png" 
                        alt="Rimuovi" 
                        className="w-3 h-3"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className={`px-6 py-4 flex items-center justify-center gap-4 ${styles.filterFooterBg}`}>
        <button
          onClick={resetFilters}
          className={`flex items-center gap-2 text-sm font-medium ${styles.resetButton}`}
        >
          <img src="/reset@2x.png" alt="Reset" className="w-5 h-auto" />
          RESET
        </button>
        
        <button
          onClick={() => {
            if (Object.keys(selectedFilters).length > 0) {
              onApplyFilters(filteredGames, selectedFilters);
            } else {
              onApplyFilters(games, {}); // Return all games if no filters selected
            }
            onClose();
          }}
          className={`px-8 py-2.5 rounded-lg text-sm ${styles.showResultsButton}`}
        >
          MOSTRA RISULTATI
          {getFilteredGamesCount() && (
            <span> ({getFilteredGamesCount()})</span>
          )}
        </button>
      </div>
    </div>
  );
}